import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { type ReactNode, useId, useRef, useState } from 'react';
import { de } from '@/i18n/de';
import { t } from '@/i18n/t';
import { Button, IconButton } from './button';

type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
};

/**
 * Dialog (Radix): Fokusfalle, Escape, Titel und Beschreibung für Vorlesegeräte. Öffnet in 150 ms; mit
 * reduzierter Bewegung sofort (globale Regel im Stylesheet der Farben und Abstände).
 */
export function Dialog({ open, onOpenChange, title, description, children, footer }: DialogProps) {
  // Ohne eigenen Auslöser (gesteuertes Öffnen) merkt sich der Dialog selbst, wohin der Fokus zurückgehört.
  const opener = useRef<HTMLElement | null>(null);
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-ink/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-150" />
        <RadixDialog.Content
          onOpenAutoFocus={() => {
            opener.current = document.activeElement as HTMLElement | null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            opener.current?.focus();
          }}
          {...(description === undefined ? { 'aria-describedby': undefined } : {})}
          className="fixed top-1/2 left-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-surface border border-line bg-surface-block p-6 text-ink shadow-xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-150"
        >
          <div className="flex items-start justify-between gap-4">
            <RadixDialog.Title className="text-h2 font-semibold">{title}</RadixDialog.Title>
            <RadixDialog.Close asChild>
              <IconButton variant="quiet" aria-label={de.ui.close}>
                <X aria-hidden="true" />
              </IconButton>
            </RadixDialog.Close>
          </div>
          {description === undefined ? null : (
            <RadixDialog.Description className="text-body text-ink-muted">
              {description}
            </RadixDialog.Description>
          )}
          {children}
          {footer === undefined ? null : (
            <div className="flex flex-wrap justify-end gap-3">{footer}</div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Nennt das Ergebnis („Endgültig löschen“), nie „OK“. */
  confirmLabel: string;
  onConfirm: () => void;
  /** Eintipp-Schutz: der Knopf geht erst, wenn genau dieses Wort eingegeben ist (z. B. „LÖSCHEN“). */
  confirmWord?: string;
  tone?: 'primary' | 'danger';
};

/** Bestätigung mit optionalem Eintipp-Schutz; der Vergleich ist exakt (Groß-/Kleinschreibung, Leerzeichen). */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  confirmWord,
  tone = 'primary',
}: ConfirmDialogProps) {
  const inputId = useId();
  const [typed, setTyped] = useState('');
  const locked = confirmWord !== undefined && typed !== confirmWord;
  const instruction = confirmWord === undefined ? '' : t('ui.typeToConfirm', { word: confirmWord });

  const close = (next: boolean) => {
    if (!next) {
      setTyped('');
    }
    onOpenChange(next);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={title}
      {...(description === undefined ? {} : { description })}
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {de.ui.cancel}
          </Button>
          <Button
            variant={tone}
            {...(locked ? { disabledReason: instruction } : {})}
            onClick={() => {
              onConfirm();
              close(false);
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {confirmWord === undefined ? null : (
        <div className="grid gap-2">
          <label htmlFor={inputId} className="text-body font-semibold">
            {de.ui.typeToConfirmLabel}
          </label>
          <p className="text-fine text-ink-muted">{instruction}</p>
          <input
            id={inputId}
            type="text"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="min-h-11 rounded-control border border-line-strong bg-surface-block px-3 text-body text-ink sm:min-h-10"
          />
        </div>
      )}
    </Dialog>
  );
}
